import { useEffect, useState, useCallback } from "react";
import { getIntegration } from "@/lib/integrations";
import { supabase } from "@/integrations/supabase/client";
import type { GangSheetSize } from "@/lib/types";
import { useAuth } from "@/hooks/use-auth";
import { dripGenerateImage } from "@/lib/gang-sheet";

const SCRIPT_URL = "https://app.buildagangsheet.com/assets/v1/scripts/gs-builder.min.js";

export async function getGangSheetSizes(): Promise<GangSheetSize[]> {
  const { data, error } = await supabase
    .from("gangsheet_sizes")
    .select("*")
    .eq("active", true)
    .order("drip_size_id");

  if (error) throw error;
  return data || [];
}

export function useGangSheetBuilderScript() {
  const { profile } = useAuth();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const [configChecked, setConfigChecked] = useState(false);
  const [shopId, setShopId] = useState<string | null>(null);

  useEffect(() => {
    // Check if integration is enabled and get shop ID
    const checkIntegration = async () => {
      try {
        const [enabled, shop] = await Promise.all([
          getIntegration("drip_enabled"),
          getIntegration("drip_shop_id"),
        ]);
        setIsEnabled(enabled === "true");
        setShopId(shop && shop.trim() !== "" ? shop.trim() : null);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to read integration config");
      } finally {
        setConfigChecked(true);
      }
    };

    checkIntegration();
  }, []);

  useEffect(() => {
    // Don't load script if not enabled
    if (!isEnabled) return;

    // Check if script is already loaded
    if (typeof window !== "undefined" && (window as any).gs_builder) {
      setIsLoaded(true);
      return;
    }

    // Check if script is already being loaded
    if (isLoading) return;

    // If a script tag for the builder already exists in the DOM (loaded by a
    // sibling component) wait for it instead of appending a duplicate.
    const existing = document.querySelector(
      `script[src="${SCRIPT_URL}"]`,
    ) as HTMLScriptElement | null;
    if (existing) {
      const onReady = () => {
        if ((window as any).gs_builder) setIsLoaded(true);
      };
      existing.addEventListener("load", onReady);
      onReady();
      return () => existing.removeEventListener("load", onReady);
    }

    setIsLoading(true);
    setError(null);

    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;

    script.onload = () => {
      setIsLoaded(true);
      setIsLoading(false);
    };

    script.onerror = () => {
      setError(
        "Could not reach the gang sheet builder script. Check your network or the API base URL in Settings → Integrations.",
      );
      setIsLoading(false);
    };

    document.head.appendChild(script);

    return () => {
      // Don't remove the script as it might be used elsewhere
    };
  }, [isLoading, isEnabled]);

  const openBuilder = useCallback(
    async (
      options: {
        design_id?: string;
        reorder?: boolean;
        product_code?: string;
        drip_size_id?: number;
        width_mm?: number;
        height_mm?: number;
        customer?: { id: string; name?: string; email?: string };
        onSaved?: (
          designId: string,
          info?: { previewUrl?: string | null; savedAt?: string; designName?: string | null },
        ) => void;
      } = {},
    ) => {
      if (!shopId) {
        throw new Error(
          "Gang sheet builder isn't configured. Ask staff to set DRIP_SHOP_ID in Settings → Integrations.",
        );
      }
      if (!isLoaded) {
        throw new Error("Gang sheet builder is still loading. Try again in a moment.");
      }

      const gs_builder = (window as any).gs_builder;
      if (!gs_builder || !gs_builder.open) {
        throw new Error(
          "Gang sheet builder script loaded but is unavailable. Reload the page and try again.",
        );
      }

      // The Drip builder requires a non-empty `sizes` array. Always fetch
      // available sizes; filter by product_code when given, otherwise pass
      // every active size so the builder still opens.
      const sizesData = await getGangSheetSizes();
      let filtered = options.product_code
        ? sizesData.filter((s) => s.product_code === options.product_code)
        : sizesData;
      if (options.drip_size_id) {
        const exact = filtered.filter((s) => s.drip_size_id === options.drip_size_id);
        if (exact.length > 0) filtered = exact;
      } else if (options.width_mm && options.height_mm) {
        // Match by mm dimensions (convert size unit to mm for comparison).
        const toMm = (v: number, unit: string) =>
          unit === "mm" ? v : unit === "cm" ? v * 10 : unit === "in" ? v * 25.4 : v;
        const match = filtered.filter(
          (s) =>
            Math.round(toMm(s.width, s.unit)) === Math.round(options.width_mm!) &&
            Math.round(toMm(s.height, s.unit)) === Math.round(options.height_mm!),
        );
        if (match.length > 0) filtered = match;
      }
      const pool = filtered.length > 0 ? filtered : sizesData;
      const sizes = pool.map((s) => ({
        id: s.drip_size_id,
        width: s.width,
        height: s.height,
        unit: s.unit,
        title: `${s.width}x${s.height} ${s.unit}`,
        metadata: {
          metres_equivalent: s.metres_equivalent,
          product_code: s.product_code,
        },
      }));

      if (sizes.length === 0) {
        throw new Error(
          "No gang sheet sizes are configured. Ask staff to add sizes in Settings → Products & Pricing → Gang sheet sizes.",
        );
      }

      const {
        width_mm: _w,
        height_mm: _h,
        drip_size_id: _id,
        onSaved,
        customer: customerOpt,
        ...passthrough
      } = options;

      // Scope designs to the current user — customer.id, name, and email are
      // required by the Drip builder to keep each user's designs private.
      const customer =
        customerOpt ??
        (profile
          ? {
              id: profile.id,
              name: profile.full_name || profile.email || profile.id,
              email: profile.email || `${profile.id}@unknown.local`,
            }
          : undefined);

      // IMPORTANT: gs_builder.open() resets eventListeners to {} internally,
      // so listeners MUST be registered AFTER open() is called.
      let builderRef: any;
      try {
        builderRef = gs_builder.open({
          shop_id: shopId,
          sizes,
          customer,
          ...passthrough,
        });
      } catch (err) {
        throw new Error(
          err instanceof Error
            ? `Builder failed to open: ${err.message}`
            : "Builder failed to open.",
        );
      }

      // Use the returned builder reference (same object as gs_builder) to
      // register events after open() so they aren't wiped.
      const b = builderRef ?? gs_builder;

      if (onSaved) {
        // Must be a plain synchronous function — the Drip builder logs a
        // "message channel closed" warning if the handler returns a Promise.
        const handleSaved = (e: any): void => {
          // Always log so production saves are diagnosable
          console.debug("[drip] event payload", JSON.stringify(e));
          // Drip sends id as a number or string under various keys depending on version
          const rawId =
            e?.data?.id ??
            e?.data?.design_id ??
            e?.data?.designId ??
            e?.data?.layout_id ??
            e?.design_id ??
            e?.designId ??
            e?.id ??
            e?.detail?.id ??
            e?.detail?.design_id ??
            null;
          // If Drip doesn't return an ID fall back to a local UUID so the cart
          // item is still created — the design exists in Drip, we just can't
          // link back to it for re-editing. This is better than blocking the user.
          const fallbackId = crypto.randomUUID();
          const id: string =
            rawId != null &&
            String(rawId) !== "undefined" &&
            String(rawId) !== "null" &&
            String(rawId) !== ""
              ? String(rawId)
              : fallbackId;
          const previewUrl: string | null =
            e?.data?.preview_url ?? e?.data?.previewUrl ?? e?.preview_url ?? e?.previewUrl ?? null;
          const designName: string | null =
            e?.data?.name ??
            e?.data?.title ??
            e?.data?.file_name ??
            e?.data?.design_name ??
            e?.name ??
            e?.title ??
            e?.file_name ??
            null;
          const savedAt: string =
            e?.data?.saved_at ??
            e?.data?.savedAt ??
            e?.saved_at ??
            e?.savedAt ??
            new Date().toISOString();
          // Close FIRST so the builder modal exits immediately
          try {
            b.close?.();
          } catch {
            /* ignore */
          }
          // Trigger server-side image generation (fire-and-forget, don't block close)
          dripGenerateImage(id).catch(() => {
            /* non-critical */
          });
          try {
            onSaved(id, { previewUrl, savedAt, designName });
          } catch {
            /* ignore consumer error */
          }
        };
        b.on?.("design:created", handleSaved);
        b.on?.("design:updated", handleSaved);
      }
    },
    [shopId, isLoaded, profile],
  );

  const status: "checking" | "disabled" | "missing-shop" | "loading" | "error" | "ready" =
    !configChecked
      ? "checking"
      : !isEnabled
        ? "disabled"
        : !shopId
          ? "missing-shop"
          : error
            ? "error"
            : isLoaded
              ? "ready"
              : "loading";

  return { isLoaded, isLoading, error, isEnabled, shopId, openBuilder, status, configChecked };
}
